import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';

export type PrePostWizardResult = {
    textType: string;
    targets: { key: string; label: string; text: string }[];
};

export const PRE_POST_TEXT_TYPES = {
    PRE: 'HEADER_PRE_TEXT',
    POST: 'HEADER_POST_TEXT'
} as const;

@Component({
    selector: 'app-prepost-text-wizard',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, ButtonModule, CheckboxModule, InputTextModule, TextareaModule],
    templateUrl: './prepost-text-wizard.component.html',
    styleUrls: ['./prepost-text-wizard.component.scss']
})
export class PrepostTextWizardComponent implements OnChanges {
    private readonly invoiceDocumentKey = 'CO03';
    private readonly allDocumentsKey = 'CO01';
    @ViewChild('okActionButton') okActionButton?: ElementRef<HTMLButtonElement>;

    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    @Input() textType: string = '';
    @Output() complete = new EventEmitter<PrePostWizardResult>();

    wizardStep = 2;
    selectedDocumentKeys: string[] = [];
    currentDocumentIndex = 0;
    documentTargets = [
        { key: 'CO03', label: 'On the Invoice' },
        { key: 'CO02', label: 'On the order confirmation' },
        { key: 'CO04', label: 'On the picking list' },
        { key: 'CO01', label: 'On ALL documents' },
        // { key: 'CO05', label: 'On the Delivery Note' }
    ];
    documentTextInputs: { key: string; label: string; text: string }[] = [];

    pageTransitionActive = false;
    private pageTransitionTimer: ReturnType<typeof setTimeout> | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible']?.currentValue === true) {
            this.resetWizardState();
        }
    }

    get selectedTypeLabel(): string {
        const normalized = (this.textType || '').trim().toUpperCase();
        const isPre = normalized === '1' || normalized.includes('PRE');
        return isPre ? 'Pre-text' : 'Post-text';
    }

    toggleDocumentTarget(targetKey: string, checked: boolean) {
        if (targetKey === this.allDocumentsKey) {
            if (checked) {
                this.selectedDocumentKeys = this.documentTargets.map((item) => item.key);
                return;
            }
            this.selectedDocumentKeys = [];
            return;
        }

        if (checked) {
            if (!this.selectedDocumentKeys.includes(targetKey)) {
                this.selectedDocumentKeys.push(targetKey);
            }
            this.syncAllDocumentsSelection();
            return;
        }
        this.selectedDocumentKeys = this.selectedDocumentKeys.filter((key) => key !== targetKey);
        this.selectedDocumentKeys = this.selectedDocumentKeys.filter((key) => key !== this.allDocumentsKey);
    }

    goToTextEntry() {
        if (!this.selectedDocumentKeys.length) return;

        const keysForEntry = this.selectedDocumentKeys.includes(this.allDocumentsKey)
            ? [this.allDocumentsKey]
            : this.selectedDocumentKeys;

        this.documentTextInputs = keysForEntry.map((key) => {
            const target = this.documentTargets.find((item) => item.key === key);
            return {
                key,
                label: target?.label || key,
                text: ''
            };
        });
        this.currentDocumentIndex = 0;
        this.wizardStep = 3;
        this.triggerTextPageTransition();
    }

    goToPreviousDocument() {
        if (this.currentDocumentIndex > 0) {
            this.currentDocumentIndex -= 1;
            this.triggerTextPageTransition();
            return;
        }

        this.wizardStep = 2;
        this.focusOkButton();
    }

    goToNextDocument() {
        if (!this.documentTextInputs[this.currentDocumentIndex]?.text?.trim()) return;

        if (this.isLastDocumentPage) {
            const targets = this.expandAllDocumentsTargetIfNeeded(this.documentTextInputs);
            this.complete.emit({
                textType: this.textType,
                targets
            });
            this.closeDialog();
            return;
        }

        this.currentDocumentIndex += 1;
        this.triggerTextPageTransition();
    }

    onNextEnter(event: Event) {
        event.preventDefault();
        if (!this.documentTextInputs[this.currentDocumentIndex]?.text?.trim()) return;
        this.goToNextDocument();
    }

    onDialogEnter(event: Event) {
        const keyboardEvent = event as KeyboardEvent;
        const isEnterPressed = keyboardEvent.key === 'Enter' || keyboardEvent.code === 'NumpadEnter';
        if (!isEnterPressed) return;

        const target = keyboardEvent.target as HTMLElement | null;

        if (this.wizardStep === 2) return;

        if (target?.tagName === 'TEXTAREA') return;

        if (this.wizardStep === 3 && this.documentTextInputs[this.currentDocumentIndex]?.text?.trim()) {
            keyboardEvent.preventDefault();
            keyboardEvent.stopPropagation();
            this.goToNextDocument();
        }
    }

    onDialogShow() {
        this.focusOkButton();
    }

    onHide() {
        this.closeDialog();
    }

    get currentDocumentTarget() {
        return this.documentTextInputs[this.currentDocumentIndex] || null;
    }

    get isLastDocumentPage() {
        return this.currentDocumentIndex === this.documentTextInputs.length - 1;
    }

    private resetWizardState() {
        this.wizardStep = 2;
        this.selectedDocumentKeys = [this.invoiceDocumentKey];
        this.documentTextInputs = [];
        this.currentDocumentIndex = 0;
        this.pageTransitionActive = false;
        if (this.pageTransitionTimer) {
            clearTimeout(this.pageTransitionTimer);
            this.pageTransitionTimer = null;
        }
    }

    private focusOkButton() {
        setTimeout(() => this.okActionButton?.nativeElement.focus(), 0);
    }

    private closeDialog() {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    private triggerTextPageTransition() {
        this.pageTransitionActive = false;
        if (this.pageTransitionTimer) {
            clearTimeout(this.pageTransitionTimer);
        }

        this.pageTransitionTimer = setTimeout(() => {
            this.pageTransitionActive = true;
            this.pageTransitionTimer = setTimeout(() => {
                this.pageTransitionActive = false;
            }, 220);
        }, 0);
    }

    private syncAllDocumentsSelection(): void {
        const nonAllKeys = this.documentTargets
            .map((item) => item.key)
            .filter((key) => key !== this.allDocumentsKey);
        const allSelected = nonAllKeys.every((key) => this.selectedDocumentKeys.includes(key));
        if (allSelected && !this.selectedDocumentKeys.includes(this.allDocumentsKey)) {
            this.selectedDocumentKeys.push(this.allDocumentsKey);
        }
    }

    private expandAllDocumentsTargetIfNeeded(targets: { key: string; label: string; text: string }[]) {
        const allTarget = targets.find((item) => item.key === this.allDocumentsKey);
        if (!allTarget) {
            return targets;
        }

        return this.documentTargets
            .filter((item) => item.key !== this.allDocumentsKey)
            .map((item) => ({
                key: item.key,
                label: item.label,
                text: allTarget.text
            }));
    }
}
